import type { CSSProperties } from 'react';
import { FieldTextAlign } from '../types/codeGeneration';

/**
 * Per-field font helpers, shared by the properties sidebar (auto-size
 * placeholder) and the Live Form Preview (CSS mapping).
 * Auto-size mirrors the backend's calculateFontSize exactly
 * (PDFGenerationService: ~74% of field height single-line, 32% multi-line,
 * clamped 8-24pt) so the UI placeholder matches what the PDF actually does.
 *
 * There is no family option. Every PDF is drawn in ONE embedded family, so
 * there is nothing per-field left to choose — only the size.
 */

/**
 * On-screen stand-in for the embedded PDF font.
 *
 * <p>Atkinson Hyperlegible is embedded in the DOCUMENT, not shipped to the browser,
 * so this only renders as the real face on a machine that happens to have it
 * installed; otherwise it falls back to the system sans-serif. The preview has always
 * been an approximation of PDFBox's metrics rather than a proof — the flattened
 * preview is the authority — and naming the real face first at least makes it exact
 * where it can be.
 */
export const PREVIEW_FONT_STACK =
  "'Atkinson Hyperlegible', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";

/**
 * Auto font size in points for a field height — mirrors backend calculateFontSize.
 *
 * The ratios are x-height-compensated for Atkinson Hyperlegible (0.496 em against
 * Helvetica's 0.523): scaled by 0.523/0.496 = 1.054, 0.70 -> 0.74 and 0.30 -> 0.32,
 * so the new face does not read ~5% smaller at the same nominal size. These MUST
 * stay identical to PDFGenerationService.calculateFontSize.
 */
export function autoFontSize(fieldHeight: number, isMultiLine: boolean): number {
  const ratio = isMultiLine ? 0.32 : 0.74;
  const size = Math.round(fieldHeight * ratio);
  return Math.max(8, Math.min(24, size));
}

/**
 * Inline style for a preview input honoring the field's overrides.
 * px ≈ pt on screen (approximation — close enough for preview purposes).
 */
export function previewFontStyle(field: { fontSize?: number; textAlign?: FieldTextAlign }): CSSProperties {
  const style: CSSProperties = {};
  if (field.fontSize && field.fontSize > 0) {
    style.fontSize = `${field.fontSize}px`;
  }
  if (field.textAlign) {
    style.textAlign = field.textAlign;
  }
  return style;
}

/**
 * Full inline style for a Live Form Preview control: the field's drawn WIDTH,
 * plus the font it will actually be rendered with.
 *
 * <h3>Why this is more than previewFontStyle</h3>
 * previewFontStyle applies a size only when the user has overridden it. On
 * "Auto" it applies none, so the preview fell back to the browser's default
 * while the PDF used calculateFontSize - the two disagreeing precisely when the
 * user had expressed no preference, which is most fields. The textarea branch
 * already worked around this with `field.fontSize ?? autoFontSize(...)`; this
 * generalises it rather than leaving one field type ahead of the others.
 *
 * <h3>On accuracy</h3>
 * autoFontSize mirrors the backend exactly, so the SIZE is right. Glyph
 * metrics are not: unless the browser happens to have Atkinson Hyperlegible
 * installed it substitutes a system face, so text that just fits here may still
 * wrap or shrink in the document. The flattened preview is the authority for
 * that. This is a close representation, not a proof.
 *
 * @param isMultiLine textarea, which the backend sizes at 30% of height rather
 *                    than 70%
 */
export function previewFieldStyle(
  field: {
    width?: number;
    height: number;
    fontSize?: number;
    textAlign?: FieldTextAlign;
  },
  isMultiLine = false
): CSSProperties {
  const style = previewFontStyle(field);

  // What the PDF will use when nothing is overridden.
  if (!style.fontSize) {
    style.fontSize = `${autoFontSize(field.height, isMultiLine)}px`;
  }
  // Always: there is one font and the author cannot override it.
  style.fontFamily = PREVIEW_FONT_STACK;

  if (field.width && field.width > 0) {
    style.width = `${field.width}px`;
    // Never wider than the card on a narrow screen, and never so narrow that
    // the control cannot be clicked or typed into. A field drawn 12pt wide is
    // legitimate on the PDF and unusable as an HTML input - fidelity loses to
    // being operable, and only for the very smallest fields.
    style.maxWidth = '100%';
    style.minWidth = '3rem';
  }

  return style;
}
